import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore, useUIStore } from '../store';

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const { showToast } = useUIStore();

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError('Authorization was denied');
      return;
    }

    if (!code) {
      setError('No authorization code received');
      return;
    }

    const exchangeCode = async () => {
      try {
        console.log('Exchanging code with API...');
        const response = await api.auth.exchangeCode(code);
        console.log('Exchange response:', response);

        if (!response.success || !response.data) {
          console.error('Exchange failed:', response.error);
          throw new Error(response.error || 'Failed to exchange code');
        }

        const { token, user } = response.data;
        console.log('Got token and user:', { token: token?.substring(0, 20) + '...', user });

        localStorage.setItem('auth_token', token);

        setUser(user);

        showToast('Successfully signed in', 'success');
        navigate('/');
      } catch (err) {
        console.error('Auth error:', err);
        setError('Failed to complete sign in');
      }
    };

    exchangeCode();
  }, [searchParams, navigate, setUser, showToast]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2">Authentication Failed</h2>
          <p className="text-slate-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="text-foretell-accent hover:underline"
          >
            Return to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-foretell-accent" />
        <p className="text-slate-400">Completing sign in...</p>
      </div>
    </div>
  );
}

export function AuthLogin() {
  useEffect(() => {
    const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
    const scope = encodeURIComponent('identify email guilds');

    window.location.href = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}`;
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-foretell-accent" />
        <p className="text-slate-400">Redirecting to Discord...</p>
      </div>
    </div>
  );
}
